import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { OBTAIN_LMS_TOKEN } from "../../Queries";
import { Course } from "../../types";

export interface Props {
  course: Course;
  onClose: () => void;
  onConnect: () => void;
  onEnable: (enabled: boolean) => void;
}

interface OAuthMessage {
  type: "OAUTH_SUCCESS" | "OAUTH_ERROR";
  code?: string;
  state?: string;
  platform?: string;
  error?: string;
}

export const ImportModal = ({
  course,
  onClose,
  onConnect,
  onEnable,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [obtainLmsToken] = useMutation(OBTAIN_LMS_TOKEN);

  async function handleOAuthSuccess(
    code: string,
    state: string,
    platform?: string,
  ) {
    setLoading(true);
    setError(null);

    try {
      const result = await obtainLmsToken({
        variables: {
          code,
          state,
        },
        context: {
          debounceKey: `obtainLmsToken`,
        },
      });

      if (result) {
        onConnect();
      }
    } catch (err) {
      console.error("OAuth token exchange failed:", err);
      setError("Failed to connect to LMS. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleOAuthMessage(event: MessageEvent<OAuthMessage | string>) {
    // Handle both old string format and new object format for backward compatibility
    if (typeof event.data === "string") {
      if (event.data === "OBTAIN_LMS_TOKEN_SUCCESSFUL") {
        onConnect();
      }
      return;
    }

    const message = event.data as OAuthMessage;

    if (message.type === "OAUTH_SUCCESS") {
      if (message.platform === "moodle") {
        // Moodle doesn't use OAuth, just signal success
        onConnect();
      } else if (message.code && message.state) {
        // Handle OAuth flow for other platforms
        handleOAuthSuccess(message.code, message.state, message.platform);
      }
    } else if (message.type === "OAUTH_ERROR") {
      setError(message.error || "OAuth connection failed");
    }
  }

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage);

    return () => {
      window.removeEventListener("message", handleOAuthMessage);
    };
  }, []);

  function onOpen() {
    setError(null); // Clear any previous errors
    window.open(
      course.oauthUrl,
      `${course.lmsType} Connect`,
      `dependent=${1}, alwaysOnTop=${1}, alwaysRaised=${1}, alwaysRaised=${1}, width=${800}, height=${450}`,
    );
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle id="import-dialog-title">
        {course.fullSyncReady
          ? "Enable automatic team sync?"
          : `Connect your ${course.lmsType} account`}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {course.fullSyncReady ? (
          <DialogContentText id="import-dialog-description">
            Would you like to enable automatic course section and group sync
            with your LMS?
          </DialogContentText>
        ) : (
          <DialogContentText id="import-dialog-description">
            We need to pair with your {course.lmsType} account to enable
            automatic course section and group sync.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {course.fullSyncReady ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => onEnable(true)} autoFocus>
              Proceed
            </Button>
          </>
        ) : (
          <Button onClick={() => onOpen()} disabled={loading}>
            {loading ? "Connecting..." : `Login with ${course.lmsType}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
